/**
 * 地区町名設定サービス
 * 地区町名を統合した1つのデータとして管理する機能を提供
 */

import { USE_MOCK_API } from '../config.js'

// モック用のサンプルデータ（地区町名を統合管理）
const mockDistrictTowns = [
  { id: 1, districtTownName: '中央地区 中央1丁目' },
  { id: 2, districtTownName: '中央地区 中央2丁目' },
  { id: 3, districtTownName: '中央地区 中央3丁目' },
  { id: 4, districtTownName: '東地区 東町1丁目' },
  { id: 5, districtTownName: '東地区 東町2丁目' },
  { id: 6, districtTownName: '西地区 西町' },
  { id: 7, districtTownName: '南地区 南町1丁目' },
  { id: 8, districtTownName: '南地区 南町2丁目' },
  { id: 9, districtTownName: '北地区 北町' }
]

/**
 * 地区町名一覧を取得
 * @returns {Promise<{success: boolean, data: Array, message?: string}>}
 */
export const getDistrictTowns = async () => {
  if (!USE_MOCK_API) {
    try {
      // 実際のAPI呼び出し（将来実装）
      const response = await fetch('/api/district-towns')
      const data = await response.json()

      if (response.ok) {
        return { success: true, data }
      } else {
        return { success: false, message: data.message || 'データの取得に失敗しました' }
      }
    } catch (error) {
      console.error('API Error:', error)
      return { success: false, message: 'サーバーとの通信に失敗しました' }
    }
  } else {
    // モックデータを使用
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          data: [...mockDistrictTowns]
        })
      }, 500) // 通信遅延をシミュレート
    })
  }
}

/**
 * 地区一覧を取得（選択肢用）
 * @returns {Promise<{success: boolean, data: Array, message?: string}>}
 */
export const getDistricts = async () => {
  if (!USE_MOCK_API) {
    try {
      const response = await fetch('/api/districts')
      const data = await response.json()

      if (response.ok) {
        return { success: true, data }
      } else {
        return { success: false, message: data.message || '地区データの取得に失敗しました' }
      }
    } catch (error) {
      console.error('API Error:', error)
      return { success: false, message: 'サーバーとの通信に失敗しました' }
    }
  } else {
    // モックデータを使用
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          data: [...mockDistricts]
        })
      }, 300)
    })
  }
}

/**
 * 新しい地区町名を追加
 * @param {Object} townData - 地区町名データ {districtTownName: string}
 * @returns {Promise<{success: boolean, data: Array, message?: string}>}
 */
export const createDistrictTown = async (townData) => {
  if (!USE_MOCK_API) {
    try {
      const response = await fetch('/api/district-towns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(townData)
      })

      const data = await response.json()

      if (response.ok) {
        return { success: true, data, message: '町名が正常に追加されました' }
      } else {
        return { success: false, message: data.message || '町名の追加に失敗しました' }
      }
    } catch (error) {
      console.error('API Error:', error)
      return { success: false, message: 'サーバーとの通信に失敗しました' }
    }
  } else {
    // モックデータで追加をシミュレート
    return new Promise((resolve) => {
      setTimeout(() => {
        // バリデーション
        if (!townData.districtTownName || !townData.districtTownName.trim()) {
          resolve({
            success: false,
            message: '地区町名の入力は必須です'
          })
          return
        }

        // 重複チェック
        const exists = mockDistrictTowns.some(town =>
          town.districtTownName === townData.districtTownName.trim()
        )

        if (exists) {
          resolve({
            success: false,
            message: '同じ地区町名が既に登録されています'
          })
          return
        }

        // 新しいIDを生成
        const newId = Math.max(...mockDistrictTowns.map(t => t.id)) + 1

        // 新しい地区町名を追加
        const newTown = {
          id: newId,
          districtTownName: townData.districtTownName.trim()
        }

        mockDistrictTowns.push(newTown)

        resolve({
          success: true,
          data: [...mockDistrictTowns],
          message: '町名が正常に追加されました'
        })
      }, 800)
    })
  }
}

/**
 * 地区町名を更新
 * @param {number} id - 地区町名ID
 * @param {Object} townData - 更新する地区町名データ {districtTownName: string}
 * @returns {Promise<{success: boolean, data: Array, message?: string}>}
 */
export const updateDistrictTown = async (id, townData) => {
  if (!USE_MOCK_API) {
    try {
      const response = await fetch(`/api/district-towns/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(townData)
      })

      const data = await response.json()

      if (response.ok) {
        return { success: true, data, message: '町名が正常に更新されました' }
      } else {
        return { success: false, message: data.message || '町名の更新に失敗しました' }
      }
    } catch (error) {
      console.error('API Error:', error)
      return { success: false, message: 'サーバーとの通信に失敗しました' }
    }
  } else {
    // モックデータで更新をシミュレート
    return new Promise((resolve) => {
      setTimeout(() => {
        const index = mockDistrictTowns.findIndex(town => town.id === id)

        if (index === -1) {
          resolve({
            success: false,
            message: '指定された町名が見つかりません'
          })
          return
        }

        // バリデーション
        if (!townData.districtTownName || !townData.districtTownName.trim()) {
          resolve({
            success: false,
            message: '地区町名の入力は必須です'
          })
          return
        }

        // 重複チェック（自分以外）
        const exists = mockDistrictTowns.some(town =>
          town.id !== id &&
          town.districtTownName === townData.districtTownName.trim()
        )

        if (exists) {
          resolve({
            success: false,
            message: '同じ地区町名が既に登録されています'
          })
          return
        }

        // 地区町名を更新
        mockDistrictTowns[index] = {
          ...mockDistrictTowns[index],
          districtTownName: townData.districtTownName.trim()
        }

        resolve({
          success: true,
          data: [...mockDistrictTowns],
          message: '町名が正常に更新されました'
        })
      }, 800)
    })
  }
}

/**
 * 地区町名を削除
 * @param {number} id - 削除する地区町名のID
 * @returns {Promise<{success: boolean, data: Array, message?: string}>}
 */
export const deleteDistrictTown = async (id) => {
  if (!USE_MOCK_API) {
    try {
      const response = await fetch(`/api/district-towns/${id}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (response.ok) {
        return { success: true, data, message: '町名が正常に削除されました' }
      } else {
        return { success: false, message: data.message || '町名の削除に失敗しました' }
      }
    } catch (error) {
      console.error('API Error:', error)
      return { success: false, message: 'サーバーとの通信に失敗しました' }
    }
  } else {
    // モックデータで削除をシミュレート
    return new Promise((resolve) => {
      setTimeout(() => {
        const index = mockDistrictTowns.findIndex(town => town.id === id)

        if (index === -1) {
          resolve({
            success: false,
            message: '指定された地区町名が見つかりません'
          })
          return
        }

        // 地区町名を削除
        mockDistrictTowns.splice(index, 1)

        resolve({
          success: true,
          data: [...mockDistrictTowns],
          message: '地区町名が正常に削除されました'
        })
      }, 600)
    })
  }
}
